import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, ShieldAlert } from 'lucide-react';
import ErrorBanner from '@/shared/components/ErrorBanner';
import {
  AUTH_ERROR_KEY,
  parseAuthError,
  type AuthErrorInfo,
} from '@/auth/utils/authErrorMessages';

const REDIRECT_SECONDS = 30;

function useAuthErrorSession(navigate: ReturnType<typeof useNavigate>) {
  const [errorInfo, setErrorInfo] = useState<AuthErrorInfo>(() =>
    parseAuthError(null)
  );
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);

  useEffect(() => {
    const rawMessage = sessionStorage.getItem(AUTH_ERROR_KEY);
    setErrorInfo(parseAuthError(rawMessage));

    if (!rawMessage) {
      navigate('/', { replace: true });
      return;
    }

    setSecondsLeft(REDIRECT_SECONDS);
    const intervalId = window.setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          window.clearInterval(intervalId);
          sessionStorage.removeItem(AUTH_ERROR_KEY);
          navigate('/', { replace: true });
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [navigate]);

  return { errorInfo, secondsLeft };
}

export default function AuthErrorFallback() {
  const navigate = useNavigate();
  const { errorInfo, secondsLeft } = useAuthErrorSession(navigate);

  const handleGoHome = () => {
    sessionStorage.removeItem(AUTH_ERROR_KEY);
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="w-full max-w-lg mx-4">
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-5">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-100 to-indigo-100 flex items-center justify-center ring-2 ring-primary-200/60 shadow-sm">
                <ShieldAlert
                  className="w-7 h-7 text-primary-700"
                  aria-hidden
                />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Error de acceso
            </h1>
            <p className="text-gray-600">
              No fue posible completar tu inicio de sesión.
            </p>
          </div>

          <ErrorBanner message={errorInfo.userMessage} />

          <div className="mt-8 space-y-4 flex flex-col items-center">
            <p className="text-sm text-gray-500 text-center">
              Redirección automática al inicio en{' '}
              <span className="font-medium text-gray-700 tabular-nums">
                {secondsLeft}
              </span>{' '}
              {secondsLeft === 1 ? 'segundo' : 'segundos'}.
            </p>
            <button
              type="button"
              onClick={handleGoHome}
              className="inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2.5 px-6 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 min-w-[220px]"
            >
              <Home className="w-4 h-4" aria-hidden />
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
